import ELK, { type ElkNode } from "elkjs/lib/elk-api"
import { TextMetrics } from "pixi.js"
import { useEffect, useState } from "react"
import {
  ALLOWED_LABELS,
  EDGE_SEGMENT_VERTICAL_LENGTH,
  LENGTH_BETWEEN_NODE_LAYERS,
  NODE_HEIGHT,
  NODE_WIDTH,
  nodeTextStyle,
  PADDING_X,
  PADDING_Y
} from "@/domains/visualization/constants/graph"
import type { Edge } from "@/domains/visualization/types/Edge"
import type { PositionedEdge, PositionedNode } from "@/domains/visualization/types/Graph"
import type { Vertex } from "@/domains/visualization/types/Vertex"

/**
 * Helper function to create an ELK graph structure from nodes and edges.
 *
 * @param nodes - Array of customer nodes to be positioned.
 * @param edges - Array of edges connecting the nodes.
 * @returns An ELK graph structure.
 */
const createElkGraph = (nodes: Vertex[], edges: Edge[]): ElkNode => {
  const filteredNodes = nodes.filter((n) => ALLOWED_LABELS.includes(n.label as (typeof ALLOWED_LABELS)[number]))

  return {
    id: "root",
    layoutOptions: {
      "elk.algorithm": "layered", // Use the layered layout algorithm
      "elk.direction": "DOWN", // Layout direction is top to bottom
      "elk.spacing.nodeNode": "30", // Spacing between nodes
      "elk.layered.spacing.nodeNodeBetweenLayers": LENGTH_BETWEEN_NODE_LAYERS.toString(), // Spacing between layers
      "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES", // Consider model order for nodes and edges
      "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX", // Node placement strategy
      "elk.layered.mergeEdges": "true" // Merges edges with the same source and target.
    },
    children: filteredNodes.map((n) => {
      const textMetrics = TextMetrics.measureText(n.name, nodeTextStyle)
      const textWidth = textMetrics.width + PADDING_X * 2
      return {
        id: n.id,
        width: Math.max(textWidth, NODE_WIDTH),
        height: Math.max(textMetrics.height + PADDING_Y * 2, NODE_HEIGHT)
      }
    }),
    edges: edges
      .filter((e) => filteredNodes.some((n) => n.id === e.outV) && filteredNodes.some((n) => n.id === e.inV))
      .map((e) => ({
        id: e.id,
        sources: [e.outV],
        targets: [e.inV]
      }))
  }
}

/**
 * Helper function to adjust node positions by applying offsets.
 *
 * @param nodes - Map of nodes to adjust.
 * @param offsetX - Horizontal offset to apply.
 * @param offsetY - Vertical offset to apply.
 */
export const adjustNodePositions = (nodes: Record<string, PositionedNode>, offsetX: number, offsetY: number) => {
  Object.values(nodes).forEach((node) => {
    node.x = Math.round(node.x + offsetX)
    node.y = Math.round(node.y + offsetY)
  })
}

/**
 * Helper function to adjust edge positions by applying offsets.
 *
 * @param edges - Array of edges to adjust.
 * @param offsetX - Horizontal offset to apply.
 * @param offsetY - Vertical offset to apply.
 * @returns Adjusted edges.
 */
export const adjustEdgePositions = (edges: PositionedEdge[], offsetX: number, offsetY: number): PositionedEdge[] => {
  return edges.map((edge) => {
    const adjustedSections = edge.sections.map((section) => ({
      startPoint: {
        x: Math.round(section.startPoint.x + offsetX),
        y: Math.round(section.startPoint.y + offsetY)
      },
      endPoint: {
        x: Math.round(section.endPoint.x + offsetX),
        y: Math.round(section.endPoint.y + offsetY)
      },
      bendPoints: section.bendPoints.map((bendPoint) => ({
        x: Math.round(bendPoint.x + offsetX),
        y: Math.round(bendPoint.y + offsetY)
      }))
    }))
    return {
      ...edge,
      sections: adjustedSections
    }
  })
}

/**
 * A custom hook that calculates the layout of a graph using the ELK layout engine.
 * It positions nodes and edges on a canvas and calculates an initial zoom level.
 *
 * @param nodes - Array of customer nodes to be positioned.
 * @param edges - Array of edges connecting the nodes.
 * @param canvasWidth - Width of the canvas where the graph will be rendered.
 * @param canvasHeight - Height of the canvas where the graph will be rendered.
 * @returns An object containing positioned nodes, positioned edges, initial zoom level and the initial layout ready state.
 */
export const useElkLayout = (
  nodes: Vertex[],
  edges: Edge[],
  canvasWidth: number,
  canvasHeight: number,
  setLayoutReady?: (ready: boolean) => void
) => {
  const [positionedNodes, setPositionedNodes] = useState<PositionedNode[]>([])
  const [positionedEdges, setPositionedEdges] = useState<PositionedEdge[]>([])
  const [initialZoom, setInitialZoom] = useState<number>(1)
  const [initialLayoutReady, setInitialLayoutReady] = useState(false)

  useEffect(() => {
    const buildGraph = async () => {
      if (!nodes.length || canvasWidth === 0 || canvasHeight === 0) return

      setLayoutReady?.(false)

      try {
        const elk = new ELK({
          workerUrl: new URL("elkjs/lib/elk-worker.min.js", import.meta.url).toString()
        })

        // Create an ELK graph structure
        const elkGraph = createElkGraph(nodes, edges)
        const layout = await elk.layout(elkGraph)

        // Map nodes to their positions
        const nodeMap: Record<string, PositionedNode> = {}

        layout.children?.forEach((node) => {
          const original = nodes.find((n) => n.id === node.id)

          if (original && node.x !== undefined && node.y !== undefined) {
            nodeMap[node.id] = {
              ...original,
              x: node.x,
              y: node.y,
              width: node.width ?? NODE_WIDTH,
              height: node.height ?? NODE_HEIGHT
            }
          }
        })

        // Map edges to their positions
        const positionedEdges =
          layout.edges
            ?.map((e) => {
              const originalEdge = edges.find((orig) => orig.id === e.id)
              const section = e.sections?.[0]

              if (!originalEdge || !section) return originalEdge

              const { startPoint, endPoint } = section

              // Always create bend points for consistent animation
              const bendPoints = [
                {
                  x: startPoint.x,
                  y: startPoint.y + EDGE_SEGMENT_VERTICAL_LENGTH
                },
                { x: endPoint.x, y: endPoint.y - EDGE_SEGMENT_VERTICAL_LENGTH }
              ]

              return {
                ...originalEdge,
                sections: [{ startPoint, endPoint, bendPoints }]
              }
            })
            .filter((edge): edge is PositionedEdge => edge !== undefined) ?? []

        // Calculate total graph dimensions
        const totalWidth = Math.max(...Object.values(nodeMap).map((node) => node.x + node.width))
        const totalHeight = Math.max(...Object.values(nodeMap).map((node) => node.y + node.height))

        // Calculate offsets to center the graph
        const offsetX = canvasWidth > totalWidth ? (canvasWidth - totalWidth) / 2 : 0
        const offsetY = canvasHeight > totalHeight ? (canvasHeight - totalHeight) / 2 : 0

        // Adjust node and edge positions
        adjustNodePositions(nodeMap, offsetX, offsetY)
        const adjustedEdges = adjustEdgePositions(positionedEdges, offsetX, offsetY)

        // Update state with positioned nodes and edges
        setPositionedNodes(Object.values(nodeMap))
        setPositionedEdges(adjustedEdges)

        // Calculate the initial zoom level
        const zoom = Math.min(canvasWidth / totalWidth, canvasHeight / totalHeight, 1)
        setInitialZoom(zoom)
        setLayoutReady?.(true)
        setInitialLayoutReady(true)
      } catch (err) {
        throw new Error(`Error in ELK layout: ${(err as Error).message}`)
      }
    }

    void buildGraph()
  }, [nodes, edges, canvasWidth, canvasHeight, setLayoutReady])

  return { positionedNodes, positionedEdges, initialZoom, initialLayoutReady }
}
